import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Search, ExternalLink, RotateCcw, ToggleLeft, ToggleRight } from 'lucide-react';
import { areToursDisabled, setToursDisabled, resetAllTours } from '../../hooks/useTour';
import {
  helpArticles,
  searchArticles,
  getArticlesForRoute,
  CATEGORY_LABELS,
  type HelpCategory,
  type HelpArticle,
} from '../../data/helpArticles';
import HelpArticleCard from './HelpArticleCard';
import './HelpPanel.css';

const ALL_CATEGORIES: HelpCategory[] = [
  'erste-schritte', 'klienten', 'termine', 'finanzen', 'methoden', 'einstellungen',
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function HelpPanel({ open, onClose }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<HelpCategory | null>(null);
  const [toursOff, setToursOff] = useState(areToursDisabled);

  // Reset search when panel opens/closes
  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveCategory(null);
    }
  }, [open]);

  // Escape key closes panel
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Context-aware suggestions
  const contextArticles = useMemo(
    () => getArticlesForRoute(location.pathname),
    [location.pathname]
  );

  // Filtered results
  const filteredArticles = useMemo(() => {
    if (query.trim()) return searchArticles(query);
    if (activeCategory) return helpArticles.filter((a) => a.category === activeCategory);
    return [];
  }, [query, activeCategory]);

  const showContext = !query.trim() && !activeCategory;

  const handleArticleClick = (article: HelpArticle) => {
    onClose();
    navigate(`/hilfe/${article.id}`);
  };

  return (
    <>
      <div
        className={`help-panel-backdrop ${open ? 'help-panel-backdrop-visible' : ''}`}
        onClick={onClose}
      />
      <div
        className={`help-panel ${open ? 'help-panel-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Hilfe-Panel"
      >
        {/* Header */}
        <div className="help-panel-header">
          <h2>Hilfe</h2>
          <button className="help-panel-close" onClick={onClose} aria-label="Schließen">
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="help-panel-search">
          <Search size={16} className="help-panel-search-icon" />
          <input
            type="text"
            placeholder="Suchen..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveCategory(null); }}
          />
        </div>

        {/* Category Pills */}
        {!query.trim() && (
          <div className="help-panel-cats">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`help-panel-cat ${activeCategory === cat ? 'help-panel-cat-active' : ''}`}
                onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
              >
                {CATEGORY_LABELS[cat]}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="help-panel-content">
          {showContext && (
            <>
              <div className="help-panel-section-label">Vorschläge für diese Seite</div>
              <div className="help-panel-list">
                {contextArticles.map((a) => (
                  <HelpArticleCard
                    key={a.id}
                    article={a}
                    compact
                    onClick={handleArticleClick}
                  />
                ))}
              </div>
            </>
          )}

          {!showContext && filteredArticles.length === 0 && (
            <div className="help-panel-empty">
              Keine Artikel gefunden.
            </div>
          )}

          {!showContext && filteredArticles.length > 0 && (
            <div className="help-panel-list">
              {filteredArticles.map((a) => (
                <HelpArticleCard
                  key={a.id}
                  article={a}
                  compact
                  onClick={handleArticleClick}
                />
              ))}
            </div>
          )}
        </div>

        {/* Onboarding & Tours Settings */}
        <div style={{
          padding: '16px',
          borderTop: '1px solid var(--border-light)',
          background: 'var(--bg)',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: !toursOff ? 10 : 0,
          }}>
            <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text)', flex: 1 }}>
              Geführte Touren
            </span>
            <button
              onClick={() => {
                const next = !toursOff;
                setToursDisabled(next);
                setToursOff(next);
              }}
              style={{
                background: toursOff ? 'var(--border)' : 'var(--info, #5B7EA8)',
                border: 'none',
                cursor: 'pointer',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: '0.78rem',
                fontWeight: 600,
                padding: '5px 12px',
                borderRadius: 20,
                transition: 'background 0.15s',
              }}
              title={toursOff ? 'Touren aktivieren' : 'Touren deaktivieren'}
            >
              {toursOff ? <ToggleLeft size={16} /> : <ToggleRight size={16} />}
              {toursOff ? 'Aus' : 'An'}
            </button>
          </div>
          {!toursOff && (
            <button
              onClick={() => {
                resetAllTours();
                localStorage.removeItem('cd365_onboarding_banner');
                onClose();
                window.location.reload();
              }}
              style={{
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '7px 12px',
                cursor: 'pointer',
                color: 'var(--text-light)',
                fontSize: '0.8rem',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
              }}
            >
              <RotateCcw size={13} />
              Alle Touren zurücksetzen
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="help-panel-footer">
          <button
            className="help-panel-all-link"
            onClick={() => { onClose(); navigate('/hilfe'); }}
          >
            <ExternalLink size={14} />
            Alle Artikel anzeigen
          </button>
        </div>
      </div>
    </>
  );
}
